import { PITCH_CONFIDENCE_MIN } from '../types';

import type { NativeF0Frame } from '../types';

/**
 * voice-gate — "이 프레임이 사람이 낸 소리인가"를 **방을 기준으로** 판정한다.
 *
 * 왜 절대 dBFS가 아닌가. 무음 게이트 상수가 다섯 번 틀렸다
 * (-60 → -45 → -35 → -60 → -45). 매번 "이 값이면 암소음이 안 걸린다"고 가정했고 매번
 * 실기기가 반박했다. 2026-08-05 계측이 이유를 보여준다
 * (`.claude/20260805/DEBUG-13-23-23.md`):
 *
 * | | rms | f0 | confidence |
 * |---|---|---|---|
 * | 암소음 | -44.6 ~ -47 | 75Hz | **0.73** |
 * | 목소리 | -39.1 ~ -29.6 | 130~220Hz | 0.54 ~ 0.70 |
 *
 * - **confidence로 못 가른다**: 암소음이 오히려 높다. 팬·전원 험은 순음에 가까워서
 *   사람 목소리보다 주기적이다.
 * - **고정 dB로도 못 가른다**: 여유가 3~4dB뿐인데, 두 분포가 기기·방·마이크 게인에
 *   따라 통째로 위아래로 옮겨간다. 어떤 상수를 골라도 다른 환경에서 틀린다.
 * - **주파수는 못 쓴다**: 암소음 75Hz(D2)가 저음 진단이 실제로 재야 하는 음역 안이다.
 *   하이패스를 올려 자르는 건 이미 시도하고 되돌렸다(진짜 저음이 같이 죽었다).
 *
 * 남는 축은 하나다: **이 방의 조용함보다 얼마나 큰가.** 상대값이라 기기·방마다 알아서
 * 맞고, 그래서 다섯 번째 상수 조정이 없다.
 *
 * 다섯 번째(-45)가 어떻게 틀렸는지가 이 표를 읽는 법을 보여준다. 방 녹음을 -47.3dBFS로
 * 합성해 재고 "-45가 방을 막는다"고 결론냈는데, 그 -47.3은 위 표의 **조용한 끝**이다.
 * 시끄러운 끝(-44.6)으로 다시 만들어 프로덕션 엔진에 물리면 방 프레임 374개가 374개
 * 전부 그려진다(midi 37.8~57.0). 상수 자체가 아니라 **상수를 고르는 축**이 틀린 것이고,
 * 그래서 그 축을 쓰는 소비처는 전부 이 게이트를 얹어야 한다.
 *
 * 네이티브 게이트를 대체하지 않고 **얹는다** — 네이티브가 무성이라 한 것을 유성으로
 * 되돌리지 않는다(양쪽 다 끄기만 한다). 네이티브의 `silence_threshold_db`는 진짜 디지털
 * 무음 바닥 역할만 남는다.
 *
 * 소비처는 둘이다: 온보딩 캡처(`useVoiceCapture`)와 디버그 콘솔의 실시간 그래프
 * (`RealtimePitchGraph`). 한동안 콘솔에는 안 걸려 있었고, 그래서 콘솔만 방을 그렸다 —
 * 게이트를 고쳐도 화면이 안 변하면 그 화면이 게이트를 안 타는 것부터 의심할 것.
 *
 * 순수 함수 + 불변 상태. 시계·난수·IO 없음 — vitest 대상.
 */

/**
 * 플로어보다 이만큼 커야 사람이 낸 소리로 본다(dB).
 *
 * 실측 간격이 암소음 -44.6, 가장 조용한 발성 -39.1로 5.5dB다. 6dB면 암소음 바로 위이자
 * 그 발성 아래에 선이 놓인다. 올리면 작게 내는 사람이 잘리고, 내리면 암소음이 샌다.
 */
export const NOISE_MARGIN_DB = 6;

/** 플로어가 조용한 쪽으로 따라 내려가는 시간상수(초). 빠르게 — 방이 조용해지면 즉시 반영. */
const FLOOR_FALL_SEC = 0.2;

/**
 * 플로어가 시끄러운 쪽으로 올라가는 시간상수(초). **아주 느리게.**
 *
 * 빠르면 긴 발성이 자기 플로어를 끌어올려 꼬리를 스스로 자른다 — 오래 버틸수록 불리해지는데
 * 지속음 진단이 재는 건 정확히 그 반대다. 게다가 올라가는 방향은 발성 프레임에서 아예
 * 멈추므로(아래 `stepVoiceGate`), 이 상수는 "잡음이 커졌을 때" 따라가는 속도만 정한다.
 */
const FLOOR_RISE_SEC = 20;

/** 프레임 간격이 이보다 벌어지면(백그라운드 복귀 등) 적응을 건너뛴다. */
const MAX_STEP_SEC = 0.2;

/**
 * 방을 재는 첫 구간(ms). 이 동안은 **판정하지 않고** 표본만 모은다.
 *
 * 바닥을 첫 프레임 하나로 정하면 안 된다: 오디오 엔진이 막 열린 직후의 몇 프레임은 입력이
 * 아직 안 올라와 사실상 무음이고, 그 값이 바닥이 되면 실제 방보다 수십 dB 낮게 잡힌다.
 * 그 상태에서는 암소음이 `+6dB` 선을 넘어 "목소리"가 되고, 한 번 그렇게 판정되면 아래
 * `stepVoiceGate`가 바닥을 **얼려서**(발성 중에는 안 올린다) 테이크 내내 못 고친다 —
 * 실측 시뮬레이션에서 300프레임 중 297개가 통과했다.
 *
 * 중앙값을 쓰는 이유가 여기 있다: 워밍업 프레임 몇 개는 소수라 중앙값을 못 움직인다.
 * 300ms(≈14프레임)면 그 소수를 이기면서, 카운트다운이 끝난 직후라 아직 아무도 소리를
 * 안 내는 구간 안에 들어간다.
 */
export const CALIBRATION_MS = 300;

/**
 * 잰 바닥이 이보다 크면 방이 아니라 **목소리를 잰 것**이다(dBFS).
 *
 * 실측 방은 -44.6 ~ -47, 실측 발성은 -39.1 ~ -18이다. 디버그 콘솔에는 카운트다운이 없어
 * 시작 버튼과 동시에 노래가 들어오고, 그러면 캘리브레이션 창이 방 대신 발성 램프를 재서
 * 바닥이 -22 같은 값으로 잠긴다 — 실기기 테이크에서 유성 221프레임 중 66개만 살아남았다
 * (나머지가 화면의 "끊긴 곡선"이다). 그래서 이 선을 넘는 바닥은 버리고 **다음 300ms 창을
 * 다시 잰다**. 바닥이 없는 동안은 엔진 판정만으로 통과시킨다 — 곡선을 통째로 지우는 것보다
 * 게이트가 잠길 때까지(숨 쉬는 첫 조용한 창) 덜 거르는 쪽이 낫다.
 *
 * 트레이드오프: 정말로 -40보다 시끄러운 방에서는 바닥이 영영 안 잠기고 상대 게이트가
 * 꺼진 채 돈다. 그런 방에서는 +6dB 마진 자체가 성립하지 않으므로 잃는 것이 없다.
 */
export const FLOOR_ROOM_MAX_DB = -40;

/**
 * 잰 바닥이 이보다 **작으면** 방이 아니라 **아직 입력이 안 올라온 것**이다(dBFS).
 *
 * 실측 방은 -44.6 ~ -47 이다. 어떤 방도 -80 아래로 조용할 수 없다 — 그 값은 마이크가 켜지기
 * 전의 디지털 무음이다. 노래 테이크가 AEC(voice processing)를 켜면서 입력 유닛을 다시 세우는
 * 동안 첫 프레임들이 정확히 그 모양으로 들어온다: 2026-09-16 실기기에서 바닥이 **-112.7dBFS**
 * 로 잠겼고, 통과선이 -106.7 이 되어 게이트가 사실상 꺼졌다. 그 테이크의 그래프는 암소음과
 * 반주 잔향까지 전부 유성으로 그려져 C3~C6 을 오갔다(`oct=22`).
 *
 * `FLOOR_ROOM_MAX_DB`와 같은 처방을 쓴다 — 이 창을 버리고 다음 300ms 를 다시 잰다. 버리는
 * 동안은 엔진 판정만으로 통과시킨다(바닥이 없는 편이 틀린 바닥보다 낫다).
 */
export const FLOOR_ROOM_MIN_DB = -80;

export interface VoiceGateState {
  /** 이 방의 조용함(dBFS). 아직 재는 중이면 null. */
  floorDb: number | null;
  lastMs: number | null;
  /** 바닥을 정하기 전에 모으는 rms 표본. 정하고 나면 비운다. */
  samples: number[];
  /** 지금 재는 창의 시작 시각. 첫 프레임(과 창을 버릴 때마다) 정해진다. */
  startMs: number | null;
  /**
   * 첫 창이 목소리로 오염돼 바닥 없이 도는 중(`FLOOR_ROOM_MAX_DB`). 이 동안은
   * 엔진 판정만으로 통과시키고, 조용한 창이 잡히면 잠근다.
   */
  ungated: boolean;
}

export const INITIAL_VOICE_GATE: VoiceGateState = {
  floorDb: null,
  lastMs: null,
  samples: [],
  startMs: null,
  ungated: false,
};

const median = (values: number[]): number => {
  const sorted = values.slice().sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2;
};

/** dt에 대한 1차 지연 계수 — 프레임 간격이 바뀌어도 같은 속도로 수렴한다. */
const decay = (dtSec: number, tauSec: number): number =>
  1 - Math.exp(-dtSec / tauSec);

/**
 * 바닥 위로 이만큼은 올라와야 "들리긴 하는데 작은 목소리"로 본다(dB) — 마진의 절반.
 * 방 소음은 바닥 근처에서 흔들리므로 바닥 높이 그대로인 프레임까지 세면 가만히 있는
 * 사용자에게 "더 크게"를 띄운다(진단 엔진 게이트 -60은 그 소음을 유성으로 넘긴다).
 */
const FAINT_ABOVE_FLOOR_DB = NOISE_MARGIN_DB / 2;

/**
 * 엔진은 유성이라는데 게이트가 버린 프레임 중 **사람이 작게 낸 소리**인가.
 * 바닥을 아직 못 쟀으면(`floorDb` null) 판정하지 않는다.
 */
export const isFaintVoice = (
  floorDb: number | null,
  frame: NativeF0Frame,
  isVoice: boolean,
): boolean =>
  !isVoice &&
  floorDb != null &&
  frame.voiced &&
  frame.confidence >= PITCH_CONFIDENCE_MIN &&
  frame.rmsDb >= floorDb + FAINT_ABOVE_FLOOR_DB;

/**
 * 프레임 하나를 흘리고 판정 + 다음 상태를 돌려준다.
 *
 * 시작은 **방을 재는 구간**이다(`CALIBRATION_MS`) — 그 동안은 판정하지 않는다.
 * 바닥이 정해진 뒤에는 **아래로는 항상, 위로는 잡음일 때만** 따라간다. 아래로 항상 가는
 * 것이 재는 구간에 소리가 섞여 바닥이 높게 잡힌 경우를 푼다. 위로 갈 때 발성을 빼는 것이
 * 긴 지속음의 꼬리를 지킨다.
 *
 * 카운트다운 없이 시작과 동시에 소리가 들어오면(디버그 콘솔) 첫 창이 발성을 재버린다 —
 * 그 창은 `FLOOR_ROOM_MAX_DB`로 알아채고 버린 뒤, 조용한 창이 잡힐 때까지 엔진 판정만
 * 쓴다(위 상수 주석 참조).
 *
 * **알려진 한계**: 그 "위로 갈 때 발성을 뺀다"가 곧 얼림이라, 테이크 도중에 방이 실제로
 * 시끄러워지면 바닥이 못 따라간다. 시작 시점의 방만 안다 — 레벨만으로는 "방이 커졌다"와
 * "사람이 부르기 시작했다"를 가를 수 없다.
 */
export const stepVoiceGate = (
  state: VoiceGateState,
  frame: NativeF0Frame,
): { state: VoiceGateState; isVoice: boolean } => {
  const rmsDb = frame.rmsDb;
  if (!Number.isFinite(rmsDb)) {
    return { state, isVoice: false };
  }

  // 방을 재는 중이다. 첫 창에서는 표본만 모으고 무성으로 둔다 — 세션은 카운트다운에
  // 열리므로 아직 소리를 낼 차례가 아니다(`CALIBRATION_MS`). 창이 목소리로 오염됐으면
  // (`FLOOR_ROOM_MAX_DB`) 버리고 다음 창을 재며, 그동안은 엔진 판정만 쓴다.
  if (state.floorDb == null || state.lastMs == null) {
    const engineSaysVoice =
      frame.voiced && frame.confidence >= PITCH_CONFIDENCE_MIN;
    const samples = [...state.samples, rmsDb];
    const startMs = state.startMs ?? frame.timestampMs;
    const done = frame.timestampMs - startMs >= CALIBRATION_MS;
    if (!done) {
      return {
        state: { ...state, lastMs: frame.timestampMs, samples, startMs },
        isVoice: state.ungated && engineSaysVoice,
      };
    }
    const floorDb = median(samples);
    // 방이 아닌 창은 양쪽 끝으로 알아본다: 목소리가 섞여 너무 크거나
    // (`FLOOR_ROOM_MAX_DB`), 입력이 아직 안 올라와 너무 작거나(`FLOOR_ROOM_MIN_DB`).
    const poisoned = floorDb > FLOOR_ROOM_MAX_DB || floorDb < FLOOR_ROOM_MIN_DB;
    return {
      state: {
        floorDb: poisoned ? null : floorDb,
        lastMs: frame.timestampMs,
        samples: [],
        startMs: poisoned ? frame.timestampMs : startMs,
        ungated: poisoned,
      },
      isVoice: poisoned
        ? state.ungated && engineSaysVoice
        : rmsDb >= floorDb + NOISE_MARGIN_DB && engineSaysVoice,
    };
  }

  const dtSec = Math.min(
    MAX_STEP_SEC,
    Math.max(0, (frame.timestampMs - state.lastMs) / 1000),
  );

  const loudEnough = rmsDb >= state.floorDb + NOISE_MARGIN_DB;
  const isVoice =
    loudEnough && frame.voiced && frame.confidence >= PITCH_CONFIDENCE_MIN;

  const falling = rmsDb < state.floorDb;
  // 발성 중에는 플로어를 올리지 않는다. 잡음이거나 조용해질 때만 움직인다.
  const tau = falling ? FLOOR_FALL_SEC : FLOOR_RISE_SEC;
  // 무음 프레임은 **적응에서 뺀다** — 그건 이 방의 조용함이 아니라 입력이 죽은 구간이다
  // (`FLOOR_ROOM_MIN_DB`). AEC 는 프레이즈 사이를 -110dBFS 로 만드는데, 하향 적응이 그걸
  // 따라가면 바닥이 방보다 수십 dB 아래로 내려가고 통과선이 암소음 밑에 선다(2026-09-16
  // 실기기: floor=-81.7, pass=-75.7, rms=-41.2 → oct=85). 바닥을 그 값으로 **클램프**하는
  // 것도 같은 결과다 — 하한(-80)에 붙은 바닥 역시 방(-44.6)을 유성으로 통과시킨다.
  // 직전에 잰 방을 그대로 들고 있는 것이 맞다.
  const floorDb =
    rmsDb < FLOOR_ROOM_MIN_DB
      ? state.floorDb
      : falling || !isVoice
        ? state.floorDb + (rmsDb - state.floorDb) * decay(dtSec, tau)
        : state.floorDb;

  return {
    state: {
      floorDb,
      lastMs: frame.timestampMs,
      samples: state.samples,
      startMs: state.startMs,
      ungated: false,
    },
    isVoice,
  };
};
